<script setup lang="ts">
  import type { NuxtError } from '#app'
  import { sanitizeBasicHtml } from '~/utils/htmlSafety'

  const props = defineProps<{
    error: NuxtError
  }>()

  const handleError = () => clearError({ redirect: '/' })
  const handleRetry = () => {
    if (import.meta.client) {
      window.location.reload()
    }
  }
  const { t } = useI18n()
  const route = useRoute()
  const localePath = useLocalePath()
  const homePath = computed(() => localePath('/'))

  const errorDescription = computed(() => {
    switch (props.error?.statusCode) {
      case 404:
        return {
          title: t('error.title.notFound'),
          message: t('error.message.notFound'),
        }
      case 403:
        return {
          title: t('error.title.forbidden'),
          message: t('error.message.forbidden'),
        }
      case 401:
        return {
          title: t('error.title.unauthorized'),
          message: t('error.message.unauthorized'),
        }
      case 400:
        return {
          title: t('error.title.badRequest'),
          message: t('error.message.badRequest'),
        }
      default:
        return {
          title: t('error.title.serverError'),
          message: t('error.message.serverError'),
        }
    }
  })
  const isServerError = computed(() => (props.error?.statusCode ?? 0) >= 500)
  const isHomeRoute = computed(() => route.path === homePath.value)
  // Our messages contain links (e.g. to the helpdesk); forwarded error messages must not
  // inject markup, so sanitize to basic formatting.
  const displayMessage = computed(() => {
    if (isServerError.value) {
      return sanitizeBasicHtml(errorDescription.value.message)
    }
    return sanitizeBasicHtml(props.error?.message ?? errorDescription.value.message)
  })
  useHead({
    title: computed(() => errorDescription.value.title),
  })
  const { isExpiredSession } = useAuthentication()
</script>

<template>
  <NuxtLayout name="error">
    <Spinner v-if="isExpiredSession" is-full-page />
    <div class="flex flex-col justify-center items-center">
      <div class="max-w-md w-full space-y-8">
        <div class="flex flex-col items-center space-y-4">
          <h2 class="text-center text-3xl font-extrabold text-primary">
            {{ props.error?.statusCode }}
          </h2>
          <!-- eslint-disable-next-line vue/no-v-html -- sanitized -->
          <p class="mt-2 text-center text-xl text-gray-600" v-html="displayMessage" />
          <BasicBtn v-if="isServerError" size="regular" @click="handleRetry">
            {{ t('error.tryAgain') }}
          </BasicBtn>
          <BasicBtn v-else-if="!isHomeRoute" size="regular" @click="handleError">
            {{ t('error.returnToHome') }}
          </BasicBtn>
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>
